/**
 * App Builder control registry - maps component types to React components and metadata.
 * Add new controls here to extend the App Builder.
 */

import DashboardTable from './components/DashboardTable';
import DashboardChart from './components/DashboardChart';
import DashboardForm from './components/DashboardForm';
import DashboardMap from './components/DashboardMap';
import DashboardRichText from './components/DashboardRichText';
import DashboardImage from './components/DashboardImage';
import DashboardGrid from './components/DashboardGrid';
import DashboardStats from './components/DashboardStats';
import DashboardList from './components/DashboardList';
import DashboardTimeline from './components/DashboardTimeline';
import DashboardPluginPanel from './components/DashboardPluginPanel';
import DashboardSkillChat from './components/DashboardSkillChat';

import appControlsGenerated from '../../../../packages/extension-kit/generated/app-controls.generated.json';

/** Extension-kit generated metadata (labels, output keys). React components remain registered here. */
export const APP_CONTROL_METADATA = appControlsGenerated.controls || [];

export const COMPONENT_TYPES = appControlsGenerated.componentTypes?.length
  ? appControlsGenerated.componentTypes
  : [
  'table',
  'chart',
  'form',
  'map',
  'rich_text',
  'image',
  'grid',
  'stats',
  'list',
  'timeline',
];

export const OUTPUT_KEYS = appControlsGenerated.outputKeys?.length
  ? appControlsGenerated.outputKeys
  : [
  'table_data',
  'chart_data',
  'form_prefill',
  'map_markers',
  'rich_text',
  'image_url',
  'grid_data',
  'stats_data',
  'list_data',
  'timeline_data',
];

export const COMPONENT_REGISTRY = {
  table: {
    component: DashboardTable,
    defaultOutputKey: 'table_data',
    label: 'Table',
  },
  chart: {
    component: DashboardChart,
    defaultOutputKey: 'chart_data',
    label: 'Chart',
  },
  form: {
    component: DashboardForm,
    defaultOutputKey: 'form_prefill',
    label: 'Form',
  },
  map: {
    component: DashboardMap,
    defaultOutputKey: 'map_markers',
    label: 'Map',
  },
  rich_text: {
    component: DashboardRichText,
    defaultOutputKey: 'rich_text',
    label: 'Rich Text',
  },
  image: {
    component: DashboardImage,
    defaultOutputKey: 'image_url',
    label: 'Image',
  },
  grid: {
    component: DashboardGrid,
    defaultOutputKey: 'grid_data',
    label: 'Grid',
  },
  stats: {
    component: DashboardStats,
    defaultOutputKey: 'stats_data',
    label: 'Stats / KPI',
  },
  list: {
    component: DashboardList,
    defaultOutputKey: 'list_data',
    label: 'List',
  },
  timeline: {
    component: DashboardTimeline,
    defaultOutputKey: 'timeline_data',
    label: 'Timeline',
  },
  plugin: {
    component: DashboardPluginPanel,
    defaultOutputKey: 'plugin_data',
    label: 'Plugin panel',
  },
  skill_chat: {
    component: DashboardSkillChat,
    defaultOutputKey: 'skill_chat',
    label: 'Skill chat',
    noFlow: true,
  },
};

APP_CONTROL_METADATA.forEach((meta) => {
  const entry = COMPONENT_REGISTRY[meta.type];
  if (entry) {
    entry.label = meta.label || entry.label;
    entry.defaultOutputKey = meta.defaultOutputKey || entry.defaultOutputKey;
  }
});

export function getComponent(type) {
  return COMPONENT_REGISTRY[type]?.component ?? null;
}

/** Panels that never run a flow (they talk to something else, like a skill). */
export function componentNeedsFlow(type) {
  return COMPONENT_REGISTRY[type]?.noFlow !== true;
}

export function getDefaultOutputKey(type) {
  return COMPONENT_REGISTRY[type]?.defaultOutputKey ?? 'table_data';
}

/** Register a custom component for App Builder extensibility */
export function registerCustomComponent(type, { component, defaultOutputKey, label }) {
  if (type && component) {
    COMPONENT_REGISTRY[type] = { component, defaultOutputKey: defaultOutputKey || 'table_data', label: label || type };
    if (!COMPONENT_TYPES.includes(type)) COMPONENT_TYPES.push(type);
    if (defaultOutputKey && !OUTPUT_KEYS.includes(defaultOutputKey)) OUTPUT_KEYS.push(defaultOutputKey);
  }
}
